// 外投容器接入：https://km.sankuai.com/collabpage/2569122918
import { isServer } from "@/lib/constant";
import { Level, reportCustomMetric } from "@/lib/owl";

const GROWTH_MSI_SCRIPT_URL = "https://s0.meituan.net/static-prod01/com.sankuai.fe.growth.meituan-files/growth-msi/2.2.0/msi-growth_2.2.0.js";

// 上报 MSI 初始化成功率
type LoadGrowthMSIErrMsg = "success" | "growthMsiNotAvailable";

function reportLoadGrowthMSISuccessRate(
  isSuccess: boolean,
  errMsg: LoadGrowthMSIErrMsg = "success"
) {
  reportCustomMetric({
    key: "loadGrowthMSI",
    value: isSuccess ? 1 : 0,
    tags: {
      errMsg,
    },
  });
}

/**
 * 加载 MSI 脚本
 * @returns Promise<void> 是否加载成功
 */
function loadGrowthMSI(): Promise<void> {
  if (isServer) {
    return Promise.reject("Growth MSI not available on server");
  }

  if (window?.FXBridge?.loadGrowthMSIPromise) return window.FXBridge.loadGrowthMSIPromise;
  (window.FXBridge = window.FXBridge || {}).loadGrowthMSIPromise = new Promise((resolve, reject) => {
    // 加载 MSI 脚本
    const script = document.createElement("script");
    script.src = GROWTH_MSI_SCRIPT_URL;
    script.onload = () => {
      resolve();
      (window.FXBridge = window.FXBridge || {}).isGrowthMSILoadFinish = true;
      reportLoadGrowthMSISuccessRate(true, "success");
    };
    script.onerror = () => {
      reject("growthMsiNotAvailable");
      (window.FXBridge = window.FXBridge || {}).isGrowthMSILoadFinish = false;
      (window.FXBridge = window.FXBridge || {}).loadGrowthMSIPromise = null;
      reportLoadGrowthMSISuccessRate(false, "growthMsiNotAvailable");
    };
    document.head.appendChild(script);
  });
  return window.FXBridge.loadGrowthMSIPromise;
}

/**
 * 加载 MSI 桥并检查是否可调用
 * @param bridgeAPI MSI 桥方法名
 * @returns Promise<boolean> 是否可调用
 */
export function checkGrowthMSIBridgeEnable(bridgeAPI: string): Promise<boolean> {
  if (isServer) {
    return Promise.reject(false);
  }

  const check = (bridgeAPI: string) => {
    if (typeof window?.Growth?.[bridgeAPI] === "function") {
      return true;
    } else {
      return false;
    }
  };

  return new Promise((resolve) => {
    if (window?.FXBridge?.isGrowthMSILoadFinish) {
      resolve(check(bridgeAPI));
    } else {
      loadGrowthMSI()
        .then(() => {
          resolve(check(bridgeAPI));
        })
        .catch(() => {
          resolve(false);
        });
    }
  });
}

/**
 * 调用 MSI 桥方法
 * @param bridgeAPI MSI 桥方法名
 * @param props 方法参数
 * @returns
 */
export function callGrowthMSIBridge(bridgeAPI: string, props?: Record<string, any>) {
  if (isServer) {
    return Promise.reject('Growth MSI not available on server');
  }

  checkGrowthMSIBridgeEnable(bridgeAPI)
    .then((enable) => {
      if (enable) {
        window?.Growth?.[bridgeAPI](props);
      } else {
        // MSI 桥不支持 API 方法
        reportError({
          name: "[callGrowthMSIBridge] msiBridgeNotSupport",
          msg: `外投容器 Growth 不支持 ${bridgeAPI} 方法`,
          category: "bridgeError",
          level: Level.ERROR,
        })
      }
    })
    .catch(() => {
      // MSI 桥调用失败
      reportError({
        name: "[callGrowthMSIBridge] msiBridgeCallFail",
        msg: `外投容器 Growth 调用 ${bridgeAPI} 方法失败`,
        category: "bridgeError",
        level: Level.ERROR,
      })
    });
}

/**
 * 初始化外投容器 Growth MSI 桥
 */
export function initGrowthMSIBridge() {
  if (isServer) return;
  if (!window?.Growth) {
    loadGrowthMSI();
  }
}
